<script setup lang="ts">
  /**
   * Persistent notice: a tinted banner that states a situation and, for the attention type,
   * offers one action. Distinct from BaseNotification, which is a dismissible alert — this one
   * has no close button because the message stays true until the user acts on it.
   *
   * `type` picks the surface: `attention` is the warm-orange "do something" banner, `error` the
   * red "this failed" one, `information` the teal "this is how things stand" one (DS 09/09b, as a
   * persistent banner — BaseNotification stays the dismissible form). Per the design system the
   * error type carries no CTA, so the `action` slot is rendered for `attention` and `information`.
   *
   * Geometry is the block's and identical in every tone (DS 1.1.129): a banner that swaps tone
   * with state must not change size. Icon and content are one flex item, `__body`, so they never
   * break apart when the CTA wraps (DS 1.1.96).
   *
   * Passing `title` promotes the message to a heading + body pair and top-aligns the icon.
   * Default slot is the message sentence, the `action` slot the CTA (usually a small BfButton),
   * the `icon` slot a page's own 24px glyph in place of the tone's default (inline SVG on
   * `currentColor`, which the slot box sets to the tone's accent).
   */
  import { computed } from 'vue'

  import BaseIcon from '@/components/ui/base/BaseIcon.vue'

  const props = withDefaults(
    defineProps<{
      type?: 'attention' | 'error' | 'information'
      title?: string
      role?: string
    }>(),
    { type: 'attention', title: '', role: '' },
  )

  // Errors announce themselves; an attention banner is ambient. An explicit `role` still wins.
  const resolvedRole = computed<string>(() => props.role || (props.type === 'error' ? 'alert' : 'status'))

  const ICON_BY_TYPE = {
    attention: 'icon-attention.svg',
    error: 'icon-error-triangle.svg',
    information: 'icon-info.svg',
  } as const

  const ACCENT_BY_TYPE = {
    attention: 'var(--bf-attention-accent)',
    error: 'var(--bf-error-accent)',
    information: 'var(--bf-info-accent)',
  } as const

  const iconName = computed<string>(() => ICON_BY_TYPE[props.type])

  const accentColor = computed<string>(() => ACCENT_BY_TYPE[props.type])
</script>

<template>
  <div
    class="notification"
    :class="[`notification--${type}`, { 'notification--with-title': title }]"
    :role="resolvedRole"
  >
    <div class="notification__body">
      <span v-if="$slots.icon" class="notification__icon notification__icon--custom" aria-hidden="true">
        <slot name="icon" />
      </span>
      <BaseIcon
        v-else
        class="notification__icon"
        :icon-name="iconName"
        :icon-color-hash="accentColor"
        width="24px"
        height="24px"
        is-without-hover
        aria-hidden="true"
      />

      <div v-if="title" class="notification__content">
        <p class="notification__title">{{ title }}</p>
        <p class="notification__text">
          <slot />
        </p>
      </div>

      <p v-else class="notification__text">
        <slot />
      </p>
    </div>

    <div v-if="type !== 'error' && $slots.action" class="notification__action">
      <slot name="action" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* Layout and type live on the block; every tone is a modifier. */
  .notification {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    padding: 14px 18px;
    border: 1px solid transparent;
    border-left: 3px solid transparent;
    border-radius: var(--bf-r-lg);
  }

  /* ---------- Tones: colour only ----------
     The icon and heading pick their own colour up from `accentColor`, so only the box
     tokens are swapped here. */

  /* Warm-orange "do something" banner. */
  .notification--attention {
    background: var(--bf-attention-bg);
    border-color: var(--bf-attention-border);
    border-left-color: var(--bf-attention-accent);
  }

  /* Red surface for the failure case. */
  .notification--error {
    background: var(--bf-error-bg);
    border-color: var(--bf-error-border);
    border-left-color: var(--bf-error-accent);
  }

  /* Teal "this is how things stand" banner. */
  .notification--information {
    background: var(--bf-info-bg);
    border-color: var(--bf-info-border);
    border-left-color: var(--bf-info-accent);
  }

  /* Icon and content as one flex item, so they stay together when the CTA wraps. */
  .notification__body {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex: 1 1 380px;
    min-width: 0;
  }

  .notification__icon {
    flex: none;
    align-self: flex-start;
  }

  /* A slotted glyph: the same 24px box, inked in the tone's accent. */
  .notification__icon--custom {
    display: block;
    width: 24px;
    height: 24px;
    color: v-bind(accentColor);
  }

  .notification__icon--custom :deep(svg) {
    display: block;
    width: 100%;
    height: 100%;
  }

  .notification__text {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    line-height: var(--bf-lh-normal);
    color: var(--bf-attention-text);
    text-wrap: pretty;
  }

  .notification--error .notification__text {
    color: var(--bf-error-text);
  }

  .notification--information .notification__text {
    color: var(--bf-info-text);
  }

  .notification__text :deep(strong) {
    font-weight: var(--bf-fw-semibold);
  }

  /* ---------- Titled variant ----------
     Same surface; the leading icon aligns to the first line and the message becomes a
     heading + body pair. */
  .notification--with-title {
    align-items: flex-start;
  }

  .notification--with-title .notification__action {
    align-self: center;
  }

  .notification__content {
    flex: 1 1 auto;
    min-width: 0;
  }

  .notification__title {
    margin: 0 0 var(--bf-sp-1);
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm_5);
    font-weight: var(--bf-fw-semibold);
    line-height: 1.35;
    letter-spacing: -0.005em;
    color: var(--bf-attention-accent);
  }

  .notification--error .notification__title {
    color: var(--bf-error-accent);
  }

  .notification--information .notification__title {
    color: var(--bf-info-accent);
  }

  .notification__action {
    flex: none;
  }

  /* Lets a CTA carry a leading icon without the page having to space it. */
  .notification__action :deep(.button__label) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* --bf-bp-md */
  @media (max-width: 700px) {
    /* Stacks: the CTA spans the full width under the message. */
    .notification__action,
    .notification__action :deep(.button) {
      width: 100%;
    }
  }
</style>
